export type SolutionMode = 'year-7' | 'year-10' | 'senior';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const SOLUTIONS_TOOL_IDENTITY = {
  id: 'solutions',
  toolKey: 'chemistry-solutions',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export type MixtureOutcome = 'solution' | 'suspension' | 'layer';

export type MixtureEvidence = 'uniform' | 'settles' | 'layers';

export type SeparationMethod = 'evaporate' | 'filter' | 'separating-funnel';

export interface FoundationMixture {
  id:
    | 'salt-water'
    | 'sand-water'
    | 'oil-water'
    | 'sugar-water'
    | 'copper-sulfate-water'
    | 'flour-water'
    | 'iron-filings-water'
    | 'paraffin-water';
  name: string;
  addedMaterial: string;
  formula?: string;
  prompt: string;
  outcome: MixtureOutcome;
  evidence: MixtureEvidence;
  evidenceExplanation: string;
  evidenceHint: string;
  observation: string;
  particleExplanation: string;
  recoveryMethod: string;
}

export const FOUNDATION_MIXTURES: FoundationMixture[] = [
  {
    id: 'salt-water',
    name: 'Salt + water',
    addedMaterial: 'Sodium chloride',
    formula: 'NaCl',
    prompt: 'What will you observe after stirring salt into water?',
    outcome: 'solution',
    evidence: 'uniform',
    evidenceExplanation: 'The liquid stays clear and looks the same throughout.',
    evidenceHint: 'Check whether any solid, cloudiness or second layer remains visible.',
    observation: 'The liquid stays clear and the salt is no longer visible.',
    particleExplanation:
      'Water separates the sodium and chloride ions and spreads them throughout the liquid.',
    recoveryMethod: 'Evaporate the water to recover salt crystals.',
  },
  {
    id: 'sand-water',
    name: 'Sand + water',
    addedMaterial: 'Sand',
    prompt: 'What will you observe after stirring sand into water?',
    outcome: 'suspension',
    evidence: 'settles',
    evidenceExplanation: 'Visible grains collect at the bottom after the mixture stands.',
    evidenceHint: 'Look at the bottom of the beaker after the mixture stands still.',
    observation: 'The grains remain visible and settle when the mixture is left still.',
    particleExplanation:
      'The sand does not dissolve. Its grains are much larger than dissolved particles and settle under gravity.',
    recoveryMethod: 'Filter the mixture to collect the sand.',
  },
  {
    id: 'oil-water',
    name: 'Oil + water',
    addedMaterial: 'Vegetable oil',
    prompt: 'What will you observe after shaking oil with water and waiting?',
    outcome: 'layer',
    evidence: 'layers',
    evidenceExplanation: 'A visible boundary separates the upper oil from the water.',
    evidenceHint: 'Look for a boundary between two liquid regions.',
    observation: 'Droplets briefly disperse, then the oil forms a separate upper layer.',
    particleExplanation:
      'Oil and water are immiscible. The less-dense oil forms the upper layer after the droplets join again.',
    recoveryMethod: 'Use a separating funnel or carefully decant one layer.',
  },
  {
    id: 'sugar-water',
    name: 'Sugar + water',
    addedMaterial: 'Table sugar (sucrose)',
    formula: 'C₁₂H₂₂O₁₁',
    prompt: 'What will you observe after stirring sugar into water?',
    outcome: 'solution',
    evidence: 'uniform',
    evidenceExplanation: 'The liquid stays clear and the same all the way through.',
    evidenceHint: 'Check whether any grains, cloudiness or a second layer remain after stirring.',
    observation: 'The sugar crystals disappear and the liquid stays clear.',
    particleExplanation:
      'Water surrounds whole sugar molecules and spreads them evenly. Unlike salt, sugar molecules do not split into ions.',
    recoveryMethod: 'Evaporate the water gently to recover sugar crystals. Strong heating would burn the sugar.',
  },
  {
    id: 'copper-sulfate-water',
    name: 'Copper sulfate + water',
    addedMaterial: 'Copper(II) sulfate crystals',
    formula: 'CuSO₄·5H₂O',
    prompt: 'What will you observe after stirring blue copper sulfate crystals into water?',
    outcome: 'solution',
    evidence: 'uniform',
    evidenceExplanation: 'The liquid is an even, see-through blue. Nothing settles and no layer forms.',
    evidenceHint: 'A colour is not cloudiness. Check whether you can see through the liquid and whether solid settles.',
    observation: 'The crystals dissolve and the whole liquid turns a clear blue.',
    particleExplanation:
      'Water separates the copper and sulfate ions and spreads them evenly. The copper ions give the solution its blue colour.',
    recoveryMethod: 'Evaporate some water, then leave the solution to crystallise. Handle copper sulfate with gloves.',
  },
  {
    id: 'flour-water',
    name: 'Flour + water',
    addedMaterial: 'Plain flour',
    prompt: 'What will you observe after stirring flour into cold water and waiting?',
    outcome: 'suspension',
    evidence: 'settles',
    evidenceExplanation: 'The cloudy liquid slowly clears as a white layer of flour collects at the bottom.',
    evidenceHint: 'Leave the mixture to stand, then look for solid collecting at the bottom.',
    observation: 'The water turns cloudy white, then flour slowly settles to the bottom.',
    particleExplanation:
      'Flour does not dissolve in cold water. Tiny starch grains spread through the water and make it cloudy, then settle under gravity.',
    recoveryMethod: 'Filter the mixture to collect the flour.',
  },
  {
    id: 'iron-filings-water',
    name: 'Iron filings + water',
    addedMaterial: 'Iron filings',
    formula: 'Fe',
    prompt: 'What will you observe after stirring iron filings into water?',
    outcome: 'suspension',
    evidence: 'settles',
    evidenceExplanation: 'The grey grains sink quickly and collect at the bottom.',
    evidenceHint: 'Look at the bottom of the beaker after you stop stirring.',
    observation: 'The grey filings swirl while you stir, then sink to the bottom.',
    particleExplanation:
      'Iron does not dissolve in water. Its grains are dense and much larger than dissolved particles, so they settle fast.',
    recoveryMethod: 'Filter the mixture, or pull the filings out with a magnet.',
  },
  {
    id: 'paraffin-water',
    name: 'Paraffin oil + water',
    addedMaterial: 'Liquid paraffin',
    prompt: 'What will you observe after shaking liquid paraffin with water and waiting?',
    outcome: 'layer',
    evidence: 'layers',
    evidenceExplanation: 'A clear boundary separates the paraffin on top from the water below.',
    evidenceHint: 'Look for a line where one liquid sits on top of the other.',
    observation: 'Droplets form while shaking, then the paraffin collects as a separate top layer.',
    particleExplanation:
      'Paraffin and water are immiscible. Paraffin is less dense than water, so it floats as the upper layer.',
    recoveryMethod: 'Use a separating funnel to run off the water layer.',
  },
];

export const MIXTURE_OUTCOME_LABELS: Record<MixtureOutcome, string> = {
  solution: 'Clear solution',
  suspension: 'Suspension then sediment',
  layer: 'Two liquid layers',
};

export const MIXTURE_EVIDENCE_LABELS: Record<MixtureEvidence, string> = {
  uniform: 'It stays clear and uniform',
  settles: 'Solid settles at the bottom',
  layers: 'A boundary forms between layers',
};

export const SEPARATION_METHOD_LABELS: Record<SeparationMethod, string> = {
  evaporate: 'Evaporate the water',
  filter: 'Filter the mixture',
  'separating-funnel': 'Use a separating funnel',
};

export function checkMixturePrediction(
  mixture: FoundationMixture,
  prediction: MixtureOutcome,
) {
  return {
    correct: prediction === mixture.outcome,
    expected: mixture.outcome,
    feedback:
      prediction === mixture.outcome
        ? mixture.observation
        : `Look for whether ${mixture.addedMaterial.toLowerCase()} dissolves, settles or forms a separate liquid layer.`,
  };
}

export function checkMixtureEvidence(
  mixture: FoundationMixture,
  evidence: MixtureEvidence,
) {
  return {
    correct: evidence === mixture.evidence,
    expected: mixture.evidence,
    feedback:
      evidence === mixture.evidence ? mixture.evidenceExplanation : mixture.evidenceHint,
  };
}

export interface TransferChallenge {
  id: 'chalk-water';
  title: string;
  scenario: string;
  question: string;
  outcome: MixtureOutcome;
  evidence: MixtureEvidence;
  separationMethod: SeparationMethod;
  explanation: string;
}

export const TRANSFER_CHALLENGE: TransferChallenge = {
  id: 'chalk-water',
  title: 'A new mixture: chalk + water',
  scenario:
    'A student shakes chalk powder with water. The liquid looks cloudy, and solid collects at the bottom after five minutes.',
  question: 'Classify the mixture and choose how to recover the chalk.',
  outcome: 'suspension',
  evidence: 'settles',
  separationMethod: 'filter',
  explanation:
    'The chalk does not dissolve. Settling shows that solid particles remain suspended before collecting at the bottom, and filtration can recover them.',
};

export function checkTransferChallenge(
  challenge: TransferChallenge,
  outcome: MixtureOutcome,
  separationMethod: SeparationMethod,
) {
  const outcomeCorrect = outcome === challenge.outcome;
  const separationCorrect = separationMethod === challenge.separationMethod;
  return {
    correct: outcomeCorrect && separationCorrect,
    outcomeCorrect,
    separationCorrect,
    feedback: outcomeCorrect
      ? separationCorrect
        ? challenge.explanation
        : 'The classification is right. Choose a method that traps an undissolved solid while water passes through.'
      : 'Use the observation as evidence: the liquid is cloudy and solid settles after standing.',
  };
}

export interface SolubilityPoint {
  temperatureC: number;
  gramsPer100gWater: number;
}

export interface SolutePreset {
  id: 'potassium-nitrate' | 'sodium-chloride' | 'potassium-chloride' | 'ammonium-chloride';
  name: string;
  formula: string;
  colour: 'sky' | 'coral';
  points: SolubilityPoint[];
  boundary: string;
}

export const SOLUBILITY_PRESETS: SolutePreset[] = [
  {
    id: 'potassium-nitrate',
    name: 'Potassium nitrate',
    formula: 'KNO₃',
    colour: 'coral',
    points: [
      { temperatureC: 0, gramsPer100gWater: 13.3 },
      { temperatureC: 20, gramsPer100gWater: 31.6 },
      { temperatureC: 40, gramsPer100gWater: 63.9 },
      { temperatureC: 60, gramsPer100gWater: 110 },
      { temperatureC: 80, gramsPer100gWater: 169 },
      { temperatureC: 100, gramsPer100gWater: 246 },
    ],
    boundary:
      'Reviewed teaching values in g solute per 100 g water; interpolation is a model, not a measured result.',
  },
  {
    id: 'sodium-chloride',
    name: 'Sodium chloride',
    formula: 'NaCl',
    colour: 'sky',
    points: [
      { temperatureC: 0, gramsPer100gWater: 35.7 },
      { temperatureC: 20, gramsPer100gWater: 36 },
      { temperatureC: 40, gramsPer100gWater: 36.5 },
      { temperatureC: 60, gramsPer100gWater: 37.3 },
      { temperatureC: 80, gramsPer100gWater: 38.4 },
      { temperatureC: 100, gramsPer100gWater: 39.8 },
    ],
    boundary:
      'Reviewed teaching values in g solute per 100 g water; real results depend on purity and method.',
  },
  {
    id: 'potassium-chloride',
    name: 'Potassium chloride',
    formula: 'KCl',
    colour: 'sky',
    points: [
      { temperatureC: 0, gramsPer100gWater: 27.8 },
      { temperatureC: 20, gramsPer100gWater: 34.0 },
      { temperatureC: 40, gramsPer100gWater: 40.0 },
      { temperatureC: 60, gramsPer100gWater: 45.6 },
      { temperatureC: 80, gramsPer100gWater: 51.2 },
      { temperatureC: 100, gramsPer100gWater: 56.2 },
    ],
    boundary:
      'Teaching values in g solute per 100 g water, converted from CRC Handbook mass-percent solubilities; interpolation is a model.',
  },
  {
    id: 'ammonium-chloride',
    name: 'Ammonium chloride',
    formula: 'NH₄Cl',
    colour: 'coral',
    points: [
      { temperatureC: 0, gramsPer100gWater: 29.4 },
      { temperatureC: 20, gramsPer100gWater: 37.5 },
      { temperatureC: 40, gramsPer100gWater: 45.9 },
      { temperatureC: 60, gramsPer100gWater: 55.3 },
      { temperatureC: 80, gramsPer100gWater: 65.6 },
      { temperatureC: 100, gramsPer100gWater: 77.3 },
    ],
    boundary:
      'Teaching values in g solute per 100 g water, converted from CRC Handbook mass-percent solubilities; interpolation is a model.',
  },
];

export interface SolubilityBracket {
  lower: SolubilityPoint;
  upper: SolubilityPoint;
  /** g solute per 100 g water, interpolated linearly between the two reviewed points. */
  value: number;
}

/** Lowest and highest temperatures in the reviewed curve; the model does not go beyond them. */
export function solubilityDataRange(preset: SolutePreset): { minC: number; maxC: number } {
  return { minC: preset.points[0].temperatureC, maxC: preset.points[preset.points.length - 1].temperatureC };
}

/**
 * The two reviewed points around a temperature and the linear interpolation between them.
 * Returns null outside the reviewed data range (fail closed, no extrapolation or clamping).
 */
export function bracketSolubility(preset: SolutePreset, temperatureC: number): SolubilityBracket | null {
  const { minC, maxC } = solubilityDataRange(preset);
  if (!Number.isFinite(temperatureC) || temperatureC < minC || temperatureC > maxC) return null;
  const upperIndex = Math.max(1, preset.points.findIndex((point) => point.temperatureC >= temperatureC));
  const upper = preset.points[upperIndex];
  const lower = preset.points[upperIndex - 1];
  const fraction = (temperatureC - lower.temperatureC) / (upper.temperatureC - lower.temperatureC);
  const value = fraction >= 1
    ? upper.gramsPer100gWater
    : lower.gramsPer100gWater + fraction * (upper.gramsPer100gWater - lower.gramsPer100gWater);
  return { lower, upper, value };
}

/** g solute per 100 g water at a temperature, or null outside the reviewed data range. */
export function interpolateSolubility(preset: SolutePreset, temperatureC: number): number | null {
  return bracketSolubility(preset, temperatureC)?.value ?? null;
}

export function solubilityRangeMessage(preset: SolutePreset): string {
  const { minC, maxC } = solubilityDataRange(preset);
  return `The reviewed ${preset.formula} data covers ${minC}–${maxC} °C. The model does not guess outside that range.`;
}

export type SaturationState = 'unsaturated' | 'saturated' | 'excess-solid';

export interface CrystalModelInput {
  preset: SolutePreset;
  soluteMassG: number;
  waterMassG: number;
  hotTemperatureC: number;
  coolTemperatureC: number;
}

export interface CrystalModelResult {
  hotLimitG: number;
  coolLimitG: number;
  dissolvedHotG: number;
  crystalsOnCoolingG: number;
  finalSolidG: number;
  hotState: SaturationState;
  prediction: 'crystals' | 'no-crystals';
}

export function saturationState(soluteMassG: number, limitG: number): SaturationState {
  if (Math.abs(soluteMassG - limitG) <= 0.05) return 'saturated';
  return soluteMassG < limitG ? 'unsaturated' : 'excess-solid';
}

/** Throws when a temperature is outside the reviewed solubility data (fail closed). */
export function runCrystalModel(input: CrystalModelInput): CrystalModelResult {
  const hotSolubility = interpolateSolubility(input.preset, input.hotTemperatureC);
  const coolSolubility = interpolateSolubility(input.preset, input.coolTemperatureC);
  if (hotSolubility === null || coolSolubility === null) throw new Error(solubilityRangeMessage(input.preset));
  const waterFactor = Math.max(0, input.waterMassG) / 100;
  const hotLimitG = hotSolubility * waterFactor;
  const coolLimitG = coolSolubility * waterFactor;
  const dissolvedHotG = Math.min(Math.max(0, input.soluteMassG), hotLimitG);
  const dissolvedCoolG = Math.min(dissolvedHotG, coolLimitG);
  const crystalsOnCoolingG = Math.max(0, dissolvedHotG - dissolvedCoolG);
  const finalSolidG = Math.max(0, input.soluteMassG - dissolvedCoolG);
  return {
    hotLimitG,
    coolLimitG,
    dissolvedHotG,
    crystalsOnCoolingG,
    finalSolidG,
    hotState: saturationState(input.soluteMassG, hotLimitG),
    prediction: crystalsOnCoolingG >= 0.1 ? 'crystals' : 'no-crystals',
  };
}

export interface DilutionInput {
  stockConcentrationMolL: number;
  aliquotVolumeMl: number;
  finalVolumeMl: number;
}

export interface DilutionResult {
  finalConcentrationMolL: number;
  molesTransferred: number;
  waterAddedMl: number;
  valid: boolean;
}

export function calculateDilution(input: DilutionInput): DilutionResult {
  const valid =
    input.stockConcentrationMolL > 0 &&
    input.aliquotVolumeMl > 0 &&
    input.finalVolumeMl >= input.aliquotVolumeMl;
  if (!valid) {
    return {
      finalConcentrationMolL: 0,
      molesTransferred: 0,
      waterAddedMl: 0,
      valid: false,
    };
  }
  const molesTransferred =
    input.stockConcentrationMolL * (input.aliquotVolumeMl / 1000);
  return {
    finalConcentrationMolL:
      (input.stockConcentrationMolL * input.aliquotVolumeMl) / input.finalVolumeMl,
    molesTransferred,
    waterAddedMl: input.finalVolumeMl - input.aliquotVolumeMl,
    valid: true,
  };
}

export function checkDilutionAnswer(expected: number, answer: number) {
  const tolerance = Math.max(0.005, expected * 0.01);
  return {
    correct: Number.isFinite(answer) && Math.abs(answer - expected) <= tolerance,
    expected,
  };
}
